import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    MediaController,
    MediaControlBar,
    MediaTimeRange,
    MediaTimeDisplay,
    MediaPlayButton,
    MediaMuteButton,
    MediaFullscreenButton,
} from "media-chrome/react";
import "./VideoClipper";

import { useTheme } from "@mui/material/styles";
import { ThemeProps } from "@styles/theme";
import { useStyles } from "./VideoPlayer.styles";

type MediaControllerEl = React.ElementRef<typeof MediaController>;

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "video-clipper": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        src?: string;
        start?: number;
        end?: number;
        slot?: string;
      };
    }
  }
}

const EPS = 0.05;

type VideoPlayerProps = {
  videoSrc: string;
  videoStart?: number;
  duration: number;
};

type ClippableMediaEl = HTMLElement & {
  currentTime: number;
  duration: number;
  paused: boolean;
  play: () => Promise<void> | void;
  pause: () => void;
};

export function VideoPlayer({ videoSrc, videoStart = 0, duration }: VideoPlayerProps) {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const controllerId = React.useId();

    const mediaRef = useRef<ClippableMediaEl | null>(null);
    const controllerRef = useRef<MediaControllerEl | null>(null);

    const [, setPlaying] = useState(false);

    const clipDuration = Math.max(0, Number(duration) || 0);
    const videoEnd = useMemo(() => videoStart + clipDuration, [videoStart, clipDuration]);

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

    const getClipEnd = useCallback(() => {
        const el = mediaRef.current;
        if (!el) {return clipDuration || 0;}

        return clipDuration > 0 ? clipDuration : Math.max(0, Number(el.duration) || 0);
    }, [clipDuration]);

    const clampToWindow = useCallback(() => {
        const el = mediaRef.current;
        if (!el) {return;}

        const clipEnd = getClipEnd();

        if (el.currentTime < 0) {
            el.currentTime = 0;
        } else if (el.currentTime > clipEnd + EPS) {
            el.currentTime = clipEnd;
            el.pause();
            setPlaying(false);
        }
    }, [getClipEnd]);

    useEffect(() => {
        const ctrl = controllerRef.current;
        if (!ctrl) {return;}

        const onSeekRequest = (e: Event) => {
            const ce = e as CustomEvent<number>;
            const relTarget = typeof ce.detail === "number" ? ce.detail : 0;

            e.preventDefault?.();
            e.stopPropagation();

            const el = mediaRef.current;
            if (!el) {return;}

            const clipEnd = getClipEnd();
            el.currentTime = clamp(relTarget, 0, clipEnd);
        };

        ctrl.addEventListener("mediaseekrequest", onSeekRequest as EventListener);

        return () => ctrl.removeEventListener("mediaseekrequest", onSeekRequest as EventListener);
    }, [getClipEnd]);

    const handleLoadedMetadata = useCallback(() => {
        const el = mediaRef.current;
        if (!el) {return;}
        el.currentTime = 0;
    }, []);

    const handleTimeUpdate = useCallback(() => {
        const el = mediaRef.current;
        if (!el) {return;}

        const clipEnd = getClipEnd();

        if (clipEnd > 0 && el.currentTime >= clipEnd - EPS) {
            el.currentTime = clipEnd;
            el.pause();
            setPlaying(false);
        }
    }, [getClipEnd]);

    const handlePlay = useCallback(() => {
        clampToWindow();
        setPlaying(true);
    }, [clampToWindow]);

    const handlePause = useCallback(() => {
        setPlaying(false);
    }, []);

    const handleSeeking = useCallback(() => {
        clampToWindow();
    }, [clampToWindow]);

    const handlePauseAndPlay = useCallback(() => {
        const el = mediaRef.current;
        if (!el) {return;}

        const clipEnd = getClipEnd();

        if (clipEnd > 0 && el.currentTime >= clipEnd - EPS) {
            el.currentTime = 0;
        }

        if (el.paused) {
            el.pause();
            setPlaying(false);
        } else {
            void el.play();
            setPlaying(true);
        }
    }, [getClipEnd]);

    return (
        <div className={styles.mainWrapper}>
            <MediaController
                id={controllerId}
                ref={controllerRef}
                className={styles.mediaController}
            >
                <video-clipper
                    slot="media"
                    ref={mediaRef as React.RefObject<any>}
                    src={videoSrc}
                    start={videoStart}
                    end={videoEnd}
                    onLoadedMetadata={handleLoadedMetadata}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onTimeUpdate={handleTimeUpdate}
                    onSeeking={handleSeeking}
                />

                <MediaControlBar className={styles.mediaControlBar}>
                    <MediaPlayButton className={styles.primaryControlButton} onClick={handlePauseAndPlay} />
                    <MediaTimeRange className={styles.timelineControl} />
                    <MediaTimeDisplay showDuration noToggle />
                    <MediaMuteButton />
                    {/* <MediaVolumeRange /> */}
                    <MediaFullscreenButton className={styles.primaryControlButton} />
                </MediaControlBar>
            </MediaController>
        </div>
    );
}

export function getDurationSeconds(ts: string | null | undefined): number {
    const m = (ts ?? "").trim().match(/^(\d+):([0-5]?\d):([0-5]?\d)(?:\.(\d+))?$/);
    if (!m) {return 0;}

    const h = Number(m[1]) || 0;
    const min = Number(m[2]) || 0;
    const sec = Number(m[3]) || 0;

    return h * 3600 + min * 60 + sec;
}

type DurationResult =
  | { kind: "vod"; seconds: number; src: string }
  | { kind: "live_or_unknown"; src: string; reason: string };

function createVideoEl() {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.playsInline = true;

    return video;
}

export function getInternetVideoDuration(
    src: string,
    timeoutMs = 15000
): Promise<DurationResult> {
    return new Promise((resolve, reject) => {
        if (!src || typeof src !== "string") {
            reject(new Error("Invalid video URL."));

            return;
        }

        const video = createVideoEl();
        let settled = false;

        const cleanup = () => {
            clearTimeout(timer);
            video.onloadedmetadata = null;
            video.onerror = null;
            video.removeAttribute("src");
            video.load();
        };

        const done = (result: DurationResult) => {
            if (settled) {return;}
            settled = true;
            cleanup();
            resolve(result);
        };

        const timer = window.setTimeout(() => {
            done({
                kind: "live_or_unknown",
                src,
                reason: "Timed out while loading metadata.",
            });
        }, timeoutMs);

        video.onloadedmetadata = () => {
            const d = video.duration;
            if (Number.isFinite(d) && d >= 0) {
                done({ kind: "vod", seconds: d, src });
            } else {
                done({
                    kind: "live_or_unknown",
                    src,
                    reason: "Duration is not finite (likely a live stream).",
                });
            }
        };

        video.onerror = () => {
            done({
                kind: "live_or_unknown",
                src,
                reason: "Could not load metadata (CORS/network/format).",
            });
        };

        video.src = src;
    });
}

export async function getVideoDurationSeconds(src: string): Promise<number> {
    const result = await getInternetVideoDuration(src);
    if (result.kind === "vod") {return result.seconds;}
    throw new Error(result.reason);
}

export async function getVideoUrlHeaders(url: string) {
    const req = new Request(url);
    const res = await fetch(req, {
        method: "GET",
        mode: "cors",
        cache: "no-store",
    });

    const headers: Record<string, string> = {};
    res.headers.forEach((value, key) => {
        headers[key] = value;
    });

    return {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        url: res.url,
        headers,
    };
}